import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useLang } from '../App'

const SITE_URL = (import.meta.env.VITE_SITE_URL || 'https://yemenheritagepeace.org').replace(/\/$/, '')
const DEFAULT_IMAGE = `${SITE_URL}/logo.png`

const pageCopy = {
  ar: {
    home: ['منظمة تراث اليمن لأجل السلام', 'منظمة يمنية تعمل على حماية وصون التراث الثقافي وتعزيز السلام والتعليم والبحث العلمي.'],
    news: ['الأخبار | منظمة تراث اليمن لأجل السلام', 'آخر الأخبار والتحديثات والمبادرات من منظمة تراث اليمن لأجل السلام.'],
    events: ['الفعاليات | منظمة تراث اليمن لأجل السلام', 'الفعاليات والأنشطة الثقافية والتعليمية لمنظمة تراث اليمن لأجل السلام.'],
    heritage: ['تراثنا حياة | منظمة تراث اليمن لأجل السلام', 'اكتشف التراث اليمني المادي واللامادي وقصصه الإنسانية.'],
    about: ['عن المنظمة | منظمة تراث اليمن لأجل السلام', 'تعرف على منظمة تراث اليمن لأجل السلام ورؤيتها ورسالتها.'],
    fields: ['مجالات العمل | منظمة تراث اليمن لأجل السلام', 'مجالات وبرامج عمل منظمة تراث اليمن لأجل السلام.'],
    contact: ['تواصل معنا | منظمة تراث اليمن لأجل السلام', 'تواصل مع منظمة تراث اليمن لأجل السلام.'],
    gallery: ['المعرض | منظمة تراث اليمن لأجل السلام', 'صور وفيديوهات توثق التراث والأنشطة اليمنية.'],
  },
  en: {
    home: ['Yemen Heritage for Peace Organization', 'A Yemeni organization protecting cultural heritage while advancing peace, education, research, and sustainable development.'],
    news: ['News | Yemen Heritage for Peace Organization', 'Latest news, updates, and initiatives from Yemen Heritage for Peace Organization.'],
    events: ['Events | Yemen Heritage for Peace Organization', 'Cultural and educational events organized by Yemen Heritage for Peace Organization.'],
    heritage: ['Heritage Life | Yemen Heritage for Peace Organization', 'Discover Yemen\'s tangible and intangible heritage and its human stories.'],
    about: ['About | Yemen Heritage for Peace Organization', 'Learn about Yemen Heritage for Peace Organization, its mission, and vision.'],
    fields: ['Our Fields | Yemen Heritage for Peace Organization', 'The programs and fields of work of Yemen Heritage for Peace Organization.'],
    contact: ['Contact | Yemen Heritage for Peace Organization', 'Contact Yemen Heritage for Peace Organization.'],
    gallery: ['Gallery | Yemen Heritage for Peace Organization', 'Photos and videos documenting Yemeni heritage and activities.'],
  },
}

function setMeta(attribute, value, content) {
  let element = document.head.querySelector(`meta[${attribute}="${value}"]`)
  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attribute, value)
    document.head.appendChild(element)
  }
  element.setAttribute('content', content)
}

function setLink(rel, href, extra = {}) {
  let element = document.head.querySelector(`link[rel="${rel}"][data-seo-managed="true"]${extra.hreflang ? `[hreflang="${extra.hreflang}"]` : ''}`)
  if (!element) {
    element = document.createElement('link')
    element.rel = rel
    element.dataset.seoManaged = 'true'
    Object.entries(extra).forEach(([key, value]) => element.setAttribute(key, value))
    document.head.appendChild(element)
  }
  element.href = href
}

function pageType(path) {
  if (path === '/') return 'home'
  if (path.startsWith('/news/')) return 'newsDetail'
  if (path === '/news') return 'news'
  if (path.startsWith('/events/')) {
    if (path.endsWith('/collections')) return 'eventCollections'
    return 'eventDetail'
  }
  if (path === '/events') return 'events'
  if (path.startsWith('/heritage-life/')) return 'heritageDetail'
  if (path === '/heritage-life') return 'heritage'
  if (path === '/about') return 'about'
  if (path === '/fields') return 'fields'
  if (path === '/contact') return 'contact'
  if (path === '/photo-gallery' || path === '/video-gallery') return 'gallery'
  return 'notFound'
}

function localizedPath(path, lang) {
  return `/${lang}${path === '/' ? '' : path}` || `/${lang}`
}

export default function Seo({ lang }) {
  const location = useLocation()
  const { seoData } = useLang() || {}

  useEffect(() => {
    const isAdmin = location.pathname.startsWith('/admin')
    const path = location.pathname.replace(/^\/(ar|en)/, '') || '/'
    const type = pageType(path)
    
    let slug = path.split('/').filter(Boolean).slice(-1)[0]
    if (type === 'eventCollections') {
      const parts = path.split('/').filter(Boolean)
      slug = parts[parts.length - 2]
    }
    
    const baseCopy = pageCopy[lang]
    let cancelled = false

    const update = (title, description, image = DEFAULT_IMAGE, jsonLd = null) => {
      if (cancelled) return
      const canonical = `${SITE_URL}${localizedPath(path, lang)}`
      const alternatePath = localizedPath(path, lang === 'ar' ? 'en' : 'ar')
      document.title = title
      setMeta('name', 'description', description)
      setMeta('name', 'robots', isAdmin || type === 'notFound' ? 'noindex, nofollow' : 'index, follow, max-image-preview:large')
      setMeta('property', 'og:type', jsonLd?.['@type'] === 'NewsArticle' ? 'article' : 'website')
      setMeta('property', 'og:title', title)
      setMeta('property', 'og:description', description)
      setMeta('property', 'og:url', canonical)
      setMeta('property', 'og:image', image || DEFAULT_IMAGE)
      setMeta('property', 'og:locale', lang === 'ar' ? 'ar_YE' : 'en_US')
      setMeta('name', 'twitter:card', 'summary_large_image')
      setMeta('name', 'twitter:title', title)
      setMeta('name', 'twitter:description', description)
      setMeta('name', 'twitter:image', image || DEFAULT_IMAGE)
      setLink('canonical', canonical)
      ;['ar', 'en', 'x-default'].forEach((alternate) => {
        const alternateLang = alternate === 'x-default' ? 'ar' : alternate
        setLink('alternate', `${SITE_URL}${localizedPath(path, alternateLang)}`, { hreflang: alternate })
      })
      document.head.querySelectorAll('script[data-seo-jsonld="true"]').forEach((script) => script.remove())
      if (jsonLd && !isAdmin) {
        const script = document.createElement('script')
        script.type = 'application/ld+json'
        script.dataset.seoJsonld = 'true'
        script.textContent = JSON.stringify(jsonLd)
        document.head.appendChild(script)
      }
    }

    if (isAdmin) {
      update(lang === 'ar' ? 'الإدارة | منظمة تراث اليمن لأجل السلام' : 'Admin | Yemen Heritage for Peace Organization', '', DEFAULT_IMAGE)
      return () => { cancelled = true }
    }

    if (type === 'notFound') {
      update(lang === 'ar' ? 'الصفحة غير موجودة | منظمة تراث اليمن لأجل السلام' : 'Page Not Found | Yemen Heritage for Peace Organization', '', DEFAULT_IMAGE)
      return () => { cancelled = true }
    }

    const copyKey = type.replace('Detail', '').replace('Collections', '')
    const [title, description] = baseCopy[copyKey] || baseCopy.home
    if (!slug || (!type.endsWith('Detail') && type !== 'eventCollections')) {
      update(title, description, DEFAULT_IMAGE, {
        '@context': 'https://schema.org', '@type': 'Organization', name: lang === 'ar' ? 'منظمة تراث اليمن لأجل السلام' : 'Yemen Heritage for Peace Organization', url: SITE_URL, logo: DEFAULT_IMAGE, sameAs: []
      })
      return () => { cancelled = true }
    }

    if (!seoData) {
      update(title, description)
      return () => { cancelled = true }
    }

    if (seoData.notFound) {
      update(title, description)
      return () => { cancelled = true }
    }

    const item = seoData
    const itemTitle = lang === 'ar' ? item.title : (item.title_en || item.title)
    const rawDescription = lang === 'ar' ? (item.content || item.title) : (item.content_en || item.content || item.title_en || item.title)
    const itemDescription = rawDescription?.replace(/\s+/g, ' ').trim().slice(0, 160) || description
    const imageValue = item.image_url || item.thumbnail_url || DEFAULT_IMAGE
    const image = /^https?:\/\//i.test(imageValue) ? imageValue : `${SITE_URL}${imageValue.startsWith('/') ? '' : '/'}${imageValue}`
    const schemaType = type === 'newsDetail' ? 'NewsArticle' : type === 'eventDetail' || type === 'eventCollections' ? 'Event' : 'Article'
    const schema = {
      '@context': 'https://schema.org', '@type': schemaType, headline: itemTitle, name: itemTitle, description: itemDescription, image: [image], url: `${SITE_URL}${localizedPath(path, lang)}`, inLanguage: lang, isPartOf: { '@type': 'WebSite', url: SITE_URL }, author: { '@type': 'Organization', name: lang === 'ar' ? 'منظمة تراث اليمن لأجل السلام' : 'Yemen Heritage for Peace Organization' }
    }
    if (type === 'eventDetail' || type === 'eventCollections') {
      schema.startDate = item.event_date || item.created_at
      if (item.location || item.location_en) schema.location = { '@type': 'Place', name: lang === 'ar' ? item.location : (item.location_en || item.location) }
    }
    update(itemTitle, itemDescription, image, schema)

    return () => { cancelled = true }
  }, [lang, location.pathname, seoData])

  return null
}
